// Editor block schema — a transport-shape mirror of the discriminated
// union defined in `@viquard/editor`. We intentionally do NOT import
// from the editor package here: contracts is FE/BE shared and must not
// pull in the editor's runtime. The closed schema in this file is the
// canonical wire validation; the editor's runtime types must stay in
// sync (changes to either side ship as a paired PR).
//
// Two surface flavours are exported:
//
//   1. `CommentBlock` / `CommentBlocks` — the legacy narrow schema accepted
//      by the comments API. Only `paragraph`, `quote-ref`, and `image`
//      blocks, flat (no nested children), strict attribute keys. This pairs
//      with the composer's `getCommentPreset()` + `allowedBlockTypes`
//      whitelist on the frontend; the API rejects anything else with
//      a structured Zod error so a malicious or stale client can't
//      smuggle in a heading / video / math block.
//
//      `CommentBody` additionally accepts DOM-Editor v2 document candidates
//      (`{ version: 2, blocks }`) for the guarded H1 comment adapter bridge.
//      The contracts package intentionally validates only transport shape; the
//      API applies the stricter comment-mode block/inline allowlist before
//      storage.
//
//   2. `EditorRefType` — the closed enum of `quote-ref.attributes.refType`
//      values currently understood by the renderer (`@viquard/editor`
//      `quote-ref` block). When new LinkableKinds want to land in the
//      comment surface, both this enum AND the editor's TYPE_LABEL map
//      must grow together.
//
// Bumping anything here is a contract change — propagate to:
//   - `apps/api/src/modules/content/comments/blocks.ts` (server validator)
//   - `apps/web/src/features/comments/views/comment-composer.tsx`
//     (composer config)
//   - `packages/editor/src/blocks/quote-ref/*` (renderer / icon)
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Reference-type enum — used by `quote-ref` blocks to pick the card icon
// + label. Maps loosely to `LinkableKind` (see `linkable-kind.ts`):
//   article    → kind 'article'
//   collection → kind 'bookmark-collection'
//   comment    → kind 'comment'
//   user       → kind 'member'
//   discussion → kind 'forum-thread'
//   post       → kind 'forum-post'
//   file       → kind 'file'
//   survey     → kind 'survey'
// ---------------------------------------------------------------------------
export const EditorRefType = z.enum([
    'article',
    'collection',
    'comment',
    'user',
    'discussion',
    'post',
    'file',
    'survey',
]);
// ---------------------------------------------------------------------------
// Hard caps on the comment block surface. The composer enforces these on
// the frontend (typing past the cap is blocked); the API re-asserts them
// at the route layer to defend against stale / hostile clients.
// ---------------------------------------------------------------------------
export const COMMENT_MAX_BLOCKS = 32;
export const COMMENT_MAX_INLINE_HTML_LEN = 8000;
export const COMMENT_MAX_PLAINTEXT_LEN = 8000;
export const COMMENT_MAX_REF_TITLE_LEN = 200;
export const COMMENT_MAX_REF_SNIPPET_LEN = 500;
export const COMMENT_MAX_REF_AUTHOR_LEN = 120;
export const COMMENT_MAX_REF_HREF_LEN = 2048;
export const COMMENT_MAX_REF_ANCHOR_LEN = 200;
// ---------------------------------------------------------------------------
const CommentAssetUrl = z
    .string()
    .min(1)
    .max(2048)
    .refine((value) => {
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    }
    catch {
        return value.startsWith('/api/');
    }
}, 'expected http(s) URL or API content path');
// ---------------------------------------------------------------------------
// Paragraph block (allowed in comments).
//
// `content` is the editor's inline HTML string. Allowed inline markup:
//   - <strong>, <em>, <code>, <u> (the editor still emits <u> from the
//     bold-toolbar; we accept it but the bubble toolbar is collapsed in
//     the comment preset)
//   - <a href="..."> (URL allowlist enforced at the API sanitiser, not
//     here — the contract just enforces shape)
//   - <span class="inline-math" data-latex="..." data-vq="inline-math"
//     contenteditable="false">…</span> (inline math; the editor stores
//     KaTeX's rendered HTML inside, but the read renderer re-renders
//     from `data-latex` and overwrites the span's innerHTML so the
//     stored inner is incidental — `data-latex` is the wire-stable bit)
//   - <span class="vq-mention" contenteditable="false" data-entity-type
//     ="user" data-entity-id="UUID">@displayName</span> (mention atom)
//   - <br>
// Anything else is stripped by the server-side `assertCommentBlocks`
// HTML walker; this Zod schema only enforces length + attribute keys.
// ---------------------------------------------------------------------------
const ParagraphAttributes = z
    .object({
    align: z.enum(['left', 'center', 'right', 'justify']).optional(),
    indent: z.number().int().min(0).max(8).optional(),
})
    .strict();
export const ParagraphCommentBlock = z
    .object({
    id: z.string().min(1).max(64),
    type: z.literal('paragraph'),
    content: z.string().max(COMMENT_MAX_INLINE_HTML_LEN),
    html: z.string().max(COMMENT_MAX_INLINE_HTML_LEN).optional(),
    attributes: ParagraphAttributes,
    children: z.array(z.never()).max(0).optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Quote-ref block (allowed in comments — paste-detection only entry-point).
//
// Snapshot fields are stored on the block at insert time so a list-render
// does not require N follow-up resolves. The `refUpdatedAt` field carries
// the resource's `updatedAt` from the resolver so cards can re-validate
// on hover (XVIII.6 freshness contract).
// ---------------------------------------------------------------------------
const QuoteRefAttributes = z
    .object({
    refType: EditorRefType,
    refId: z.string().min(1).max(128),
    refTitle: z.string().min(1).max(COMMENT_MAX_REF_TITLE_LEN),
    refSnippet: z.string().max(COMMENT_MAX_REF_SNIPPET_LEN).optional(),
    refAuthor: z.string().max(COMMENT_MAX_REF_AUTHOR_LEN).optional(),
    refHref: z.string().min(1).max(COMMENT_MAX_REF_HREF_LEN),
    refBlockAnchor: z.string().max(COMMENT_MAX_REF_ANCHOR_LEN).optional(),
    refUpdatedAt: z.string().datetime({ offset: true }).optional(),
})
    .strict();
export const QuoteRefCommentBlock = z
    .object({
    id: z.string().min(1).max(64),
    type: z.literal('quote-ref'),
    /** Quote-ref blocks are void; the editor stores empty `content`. */
    content: z.literal(''),
    html: z.string().max(COMMENT_MAX_INLINE_HTML_LEN).optional(),
    attributes: QuoteRefAttributes,
    children: z.array(z.never()).max(0).optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Image block (allowed in comments). Images are content, not attachments:
// they render inline with the surrounding prose, while `comments.attachments`
// is reserved for downloadable non-image files.
// ---------------------------------------------------------------------------
const ImageAttributes = z
    .object({
    src: CommentAssetUrl,
    srcKey: z.string().max(512).optional(),
    alt: z.string().max(200).optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    caption: z.string().max(500).optional(),
    opacity: z.number().int().min(0).max(100).optional(),
    borderRadius: z.number().int().min(0).max(64).optional(),
    float: z.enum(['left', 'right', 'none']).optional(),
    floatWidth: z.string().max(32).optional(),
})
    .strict();
export const ImageCommentBlock = z
    .object({
    id: z.string().min(1).max(64),
    type: z.literal('image'),
    content: z.string().max(500),
    html: z.string().max(COMMENT_MAX_INLINE_HTML_LEN).optional(),
    attributes: ImageAttributes,
    children: z.array(z.never()).max(0).optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Discriminated union — exhaustive list of block kinds the comment
// surface accepts.
// ---------------------------------------------------------------------------
export const CommentBlock = z.discriminatedUnion('type', [
    ParagraphCommentBlock,
    QuoteRefCommentBlock,
    ImageCommentBlock,
]);
// ---------------------------------------------------------------------------
// CommentBlocks — full comment body. Non-empty, capped at
// `COMMENT_MAX_BLOCKS` so the client can't ship a 10k-block comment.
// The route-level service additionally rejects an "all-empty paragraphs"
// payload (a body with non-zero blocks but zero plaintext after walking).
// ---------------------------------------------------------------------------
export const CommentBlocks = z.array(CommentBlock).min(1).max(COMMENT_MAX_BLOCKS);
export const DOMEditorCommentBlock = z.lazy(() => z
    .object({
    id: z.string().min(1).max(64),
    type: z.string().min(1).max(32),
    attrs: z.record(z.string(), z.unknown()),
    content: z.array(z.unknown()).optional(),
    children: z.array(DOMEditorCommentBlock).optional(),
})
    .passthrough());
export const DOMEditorCommentBody = z
    .object({
    version: z.literal(2),
    blocks: z.array(DOMEditorCommentBlock).min(1).max(COMMENT_MAX_BLOCKS),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
export const CommentBody = z.union([CommentBlocks, DOMEditorCommentBody]);
//# sourceMappingURL=editor-block.js.map